import { motion } from "framer-motion";
import { FileText, CalendarCheck, CreditCard, ArrowRight } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { useT } from "@/i18n";

const BOOKING_URL = "https://cal.eu/robertabarbierato";

export default function Home() {
  const { t } = useT();
  const tiles = [
    { testid: "tile-documenti", icon: FileText, title: t("home.doc.title"), desc: t("home.doc.desc"), href: "/documenti", external: false },
    { testid: "tile-appuntamento", icon: CalendarCheck, title: t("home.app.title"), desc: t("home.app.desc"), href: BOOKING_URL, external: true },
    { testid: "tile-pagamento", icon: CreditCard, title: t("home.pay.title"), desc: t("home.pay.desc"), href: "/pagamento", external: false },
  ];

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <AppHeader />

      <main className="mx-auto max-w-md px-5 pb-10 pt-12">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#C9A84C]">{t("home.eyebrow")}</span>
          <h1 className="mt-3 font-serif text-[2.6rem] font-medium leading-[1.05] text-[#1a1a1a] sm:text-5xl">
            {t("home.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-[#4a4a4a]">{t("home.subtitle")}</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-4">
          {tiles.map((tile, i) => (
            <motion.a
              key={tile.testid}
              href={tile.href}
              data-testid={tile.testid}
              target={tile.external ? "_blank" : undefined}
              rel={tile.external ? "noopener noreferrer" : undefined}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 * (i + 1) }}
              className="group flex items-center gap-4 rounded-2xl border border-[#C9A84C]/25 bg-white p-5 shadow-[0_1px_3px_rgba(26,26,26,0.04)] transition-all hover:-translate-y-[2px] hover:border-[#C9A84C]/60 hover:shadow-[0_10px_28px_rgba(201,168,76,0.14)]"
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#C9A84C]/25 bg-[#C9A84C]/[0.07] transition-colors group-hover:bg-[#C9A84C]/15">
                <tile.icon className="h-6 w-6 text-[#C9A84C]" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-xl font-medium tracking-tight text-[#1a1a1a]">{tile.title}</div>
                <div className="mt-0.5 text-sm leading-snug text-[#909090]">{tile.desc}</div>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#c9c1ad] transition-all group-hover:translate-x-0.5 group-hover:text-[#C9A84C]" strokeWidth={1.5} />
            </motion.a>
          ))}
        </div>
      </main>

      <footer data-testid="app-footer" className="mt-6 border-t border-[#C9A84C]/20 px-5 pb-8 pt-8 text-center">
        <a
          href="https://robertabarbierato.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-site-link"
          className="font-serif text-lg font-medium tracking-tight text-[#1a1a1a] transition-colors hover:text-[#C9A84C]"
        >
          robertabarbierato.com
        </a>
        <p data-testid="footer-copyright" className="mt-2 text-[11px] font-medium uppercase tracking-[0.22em] text-[#909090]">
          © 2026 RB Studio
        </p>
        <a
          href="/admin"
          data-testid="staff-link"
          className="mt-5 inline-block text-[10px] font-medium uppercase tracking-[0.18em] text-[#c9c1ad] transition-colors hover:text-[#C9A84C]"
        >
          {t("home.staff")}
        </a>
      </footer>
    </div>
  );
}
